<div class="vendor-credit-upload p-3" *ngIf="vendor">
	<h5 class="mb-3">Upload Credit</h5>
	<p class="text-muted small">Upload a CSV or Excel file, map columns to reference, dates, total or GL line details (gl_account, gl_name, line_amount). Build drafts on screen, edit headers and line items, then create credits.</p>

	<div class="mb-3">
		<div class="d-flex align-items-center flex-wrap gap-2 mb-2">
			<button mat-stroked-button (click)="downloadCreditUploadTemplate()">
				<mat-icon>file_download</mat-icon> Download template
			</button>
		</div>
		<ngx-dropzone class="vendor-upload-dropzone" [multiple]="true" [accept]="'.csv,text/csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'" (change)="onDropzoneChange($event)" [disabled]="processing">
			<ngx-dropzone-label>DROP FILE HERE OR CLICK TO UPLOAD</ngx-dropzone-label>
			<ngx-dropzone-preview *ngFor="let f of files" [removable]="true" (removed)="onRemove(f)">
				<ngx-dropzone-label>{{ f.name }} ({{ f.type }})</ngx-dropzone-label>
			</ngx-dropzone-preview>
		</ngx-dropzone>
		<div *ngIf="processing" class="mt-2">
			<svg class="spinner" width="24" height="24" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
				<circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
			</svg> Parsing…
		</div>
	</div>

	<div *ngIf="uploadReady && uploadedRawData.length" class="table-responsive">
		<table class="table table-sm table-striped">
			<thead>
				<tr>
					<th *ngFor="let h of uploadedHeaders" class="small">
						<mat-form-field appearance="outline" class="w-100" style="margin-bottom: -1.25em;">
							<mat-select [(value)]="columnMappings[h]" (selectionChange)="columnMappings[h] = $event.value">
								<mat-option *ngFor="let type of availableFieldTypes" [value]="type">{{ type }}</mat-option>
							</mat-select>
						</mat-form-field>
						<div class="small text-muted">{{ h }}</div>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr *ngFor="let row of uploadedRawData; let i = index" [class.table-primary]="i < 3">
					<td *ngFor="let h of uploadedHeaders">{{ row[h] }}</td>
				</tr>
			</tbody>
		</table>
		<div class="d-flex flex-wrap align-items-center mt-3 gap-2">
			<button mat-raised-button color="accent" (click)="buildRows()" [disabled]="!hasRequiredMappings()">
				Build lines
			</button>
			<button mat-stroked-button (click)="clearAll()">Clear</button>
		</div>

		<div *ngIf="builtDrafts.length" class="mt-4">
			<h6 class="mb-2">Credit drafts</h6>
			<div class="table-responsive">
				<table class="table table-sm table-bordered">
					<thead>
						<tr>
							<th></th>
							<th>Reference</th>
							<th>Credit date</th>
							<th>Due date</th>
							<th>Total</th>
							<th>Discount amt</th>
							<th>Discount date</th>
							<th>Rate</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						<ng-container *ngFor="let draft of builtDrafts; let i = index">
							<tr>
								<td>
									<button mat-icon-button (click)="toggleDraftExpanded(i)" [attr.aria-label]="expandedDraftIndex === i ? 'Collapse' : 'Expand'">
										<mat-icon>{{ expandedDraftIndex === i ? 'expand_less' : 'expand_more' }}</mat-icon>
									</button>
								</td>
								<td><input class="form-control form-control-sm" [(ngModel)]="draft.reference" placeholder="reference" /></td>
								<td><input class="form-control form-control-sm" type="date" [ngModel]="draft.creditDate | date:'yyyy-MM-dd'" (ngModelChange)="setDraftCreditDate(draft, $event)" /></td>
								<td><input class="form-control form-control-sm" type="date" [ngModel]="draft.dueDate | date:'yyyy-MM-dd'" (ngModelChange)="setDraftDueDate(draft, $event)" /></td>
								<td>{{ draftTotal(draft) | number:'1.2-2' }}</td>
								<td><input class="form-control form-control-sm" type="number" step="0.01" [(ngModel)]="draft.discountAmount" placeholder="discount" /></td>
								<td><input class="form-control form-control-sm" type="date" [ngModel]="(draft.discountDate || draft.dueDate) | date:'yyyy-MM-dd'" (ngModelChange)="setDraftDiscountDate(draft, $event)" /></td>
								<td><input class="form-control form-control-sm" type="number" step="0.01" [(ngModel)]="draft.rate" placeholder="rate" /></td>
								<td><button mat-icon-button color="warn" (click)="removeCreditDraft(i)" matTooltip="Remove draft"><mat-icon>delete</mat-icon></button></td>
							</tr>
							<tr *ngIf="expandedDraftIndex === i">
								<td colspan="9" class="p-3 bg-light">
									<div class="mb-2"><strong>GL charge lines</strong></div>
									<table class="table table-sm table-bordered mb-2">
										<thead>
											<tr>
												<th>Account</th>
												<th>Name</th>
												<th>Amount</th>
												<th></th>
											</tr>
										</thead>
										<tbody>
											<tr *ngFor="let line of draft.glcharges; let j = index">
												<td><input class="form-control form-control-sm" [(ngModel)]="line.account" placeholder="GL account" /></td>
												<td><input class="form-control form-control-sm" [(ngModel)]="line.name" placeholder="description" /></td>
												<td><input class="form-control form-control-sm" type="number" step="0.01" [(ngModel)]="line.value" placeholder="amount" /></td>
												<td><button mat-icon-button color="warn" (click)="removeGlCharge(draft, j)" matTooltip="Remove line"><mat-icon>delete</mat-icon></button></td>
											</tr>
										</tbody>
									</table>
									<button mat-stroked-button (click)="addGlCharge(draft)"><mat-icon>add</mat-icon> Add line</button>
								</td>
							</tr>
						</ng-container>
					</tbody>
				</table>
			</div>
			<div class="d-flex flex-wrap align-items-center mt-2 gap-2">
				<button mat-stroked-button (click)="addCreditDraft()"><mat-icon>add</mat-icon> Add draft</button>
				<button mat-raised-button color="primary" (click)="createCredits()" [disabled]="creating">
					<ng-container *ngIf="!creating">Create Credits</ng-container>
					<ng-container *ngIf="creating">Creating {{ progressCurrent }} of {{ progressTotal }}…</ng-container>
				</button>
				<span class="text-success" *ngIf="uploadSuccess">{{ uploadSuccess }}</span>
				<span class="text-danger" *ngIf="uploadError">{{ uploadError }}</span>
			</div>
			<div *ngIf="errorRows.length" class="mt-2">
				<small class="text-danger">Errors:</small>
				<ul class="small mb-0">
					<li *ngFor="let e of errorRows">Row {{ e.row }}: {{ e.message }}</li>
				</ul>
			</div>
		</div>
	</div>
</div>

<div *ngIf="!vendor" class="p-3 text-muted">Select a vendor to upload credits.</div>
